import { absoluteApiUrl, request, requestData, requestPage } from '@/lib/api-client';
import type {
  BatchPickupResult,
  DeliveryDetail,
  DeliveryJob,
  PickupResult,
  StoredFile,
} from './types';

/**
 * The run sheet and the transitions along it.
 *
 * Every transition endpoint answers with the WHOLE updated delivery, which is
 * why nothing here refetches after a write — the caller redraws from the
 * response. One round trip instead of two, on the worst connection in the
 * system.
 */
export const deliveriesApi = {
  /**
   * The run sheet.
   *
   * No rider filter is sent and none would be honoured: the backend scopes
   * this to the caller, and a rider asking for the unassigned queue is given
   * their own jobs instead — that queue is dispatch's to hand out, not a
   * rider's to shop from.
   *
   * `active=true` is applied server-side, which is what keeps yesterday's
   * completed runs from pushing today's work off the first page within a day.
   */
  async list(options: { activeOnly?: boolean; limit?: number } = {}): Promise<DeliveryJob[]> {
    const page = await requestPage<DeliveryJob>('/deliveries', {
      query: {
        active: options.activeOnly ? 'true' : undefined,
        limit: options.limit ?? 30,
      },
    });
    return page.data;
  },

  async get(deliveryId: string): Promise<DeliveryDetail> {
    return hydrate(await requestData<DeliveryDetail>(`/deliveries/${deliveryId}`));
  },

  accept: (deliveryId: string) => advance(deliveryId, 'accept'),
  arrivedAtPharmacy: (deliveryId: string) => advance(deliveryId, 'arrived-at-pharmacy'),
  startDelivering: (deliveryId: string) => advance(deliveryId, 'out-for-delivery'),
  arrivedAtCustomer: (deliveryId: string) => advance(deliveryId, 'arrived-at-customer'),

  /**
   * Collecting the bag.
   *
   * Returns the handover code alongside the delivery, once and never again —
   * see `PickupResult`. Refused with a 409 until the pharmacy has marked the
   * leg ready, which is what stops an order being recorded as collected while
   * it is still on the counter.
   */
  async pickUp(deliveryId: string): Promise<PickupResult> {
    const envelope = await request<{ data: DeliveryDetail; customerOtp?: string }>(
      `/deliveries/${deliveryId}/pickup`,
      { method: 'POST' },
    );
    return { delivery: hydrate(envelope.data), customerOtp: envelope.customerOtp ?? null };
  },

  /**
   * Several bags off one counter, in one request.
   *
   * Answers 200 even when nothing could be collected: an unpacked bag is a
   * refusal the rider acts on, not a failed request, and every entry in
   * `skipped` carries the server's own sentence about why it was left. The
   * caller must render BOTH halves — showing only what was collected is how a
   * rider rides off believing they have three bags and has two.
   */
  pickUpMany(deliveryIds: string[]): Promise<BatchPickupResult> {
    return requestData<BatchPickupResult>('/deliveries/pickup', {
      method: 'POST',
      body: { deliveryIds },
      // One request, but N transactions behind it. A batch of six on a shop's
      // wifi needs longer than a single pickup does.
      timeoutMs: 45_000,
    });
  },

  /**
   * The photograph at the door.
   *
   * Two steps, deliberately: the file lands first and returns a storage key,
   * then the handover is recorded with it. One endpoint doing both would
   * attach a photograph to a delivery that then failed its OTP check — and
   * leave a picture of someone's doorstep in the bucket with nothing pointing
   * at it.
   *
   * Given a long timeout because it is the one request in the app that carries
   * a payload, and it is sent from a stairwell.
   */
  async uploadProof(deliveryId: string, file: File): Promise<string> {
    const form = new FormData();
    form.append('file', file, file.name);
    const stored = await requestData<StoredFile>(`/deliveries/${deliveryId}/proof`, {
      method: 'POST',
      form,
      timeoutMs: 60_000,
    });
    return stored.key;
  },

  /**
   * The handover itself. The code is the customer's; the key, if there is one,
   * came from `uploadProof`.
   */
  async deliver(
    deliveryId: string,
    input: { otp: string; proofOfDeliveryKey?: string | null },
  ): Promise<DeliveryDetail> {
    return hydrate(
      await requestData<DeliveryDetail>(`/deliveries/${deliveryId}/deliver`, {
        method: 'POST',
        body: {
          otp: input.otp,
          ...(input.proofOfDeliveryKey ? { proofOfDeliveryKey: input.proofOfDeliveryKey } : {}),
        },
      }),
    );
  },

  /**
   * Given up on.
   *
   * Only reachable once the medicine is in hand — before pickup the state
   * machine has no edge to FAILED, because nothing has changed hands and the
   * job simply goes back to dispatch to reassign.
   */
  async fail(deliveryId: string, reason: string): Promise<DeliveryDetail> {
    return hydrate(
      await requestData<DeliveryDetail>(`/deliveries/${deliveryId}/fail`, {
        method: 'POST',
        body: { reason },
      }),
    );
  },
};

async function advance(deliveryId: string, action: string): Promise<DeliveryDetail> {
  return hydrate(
    await requestData<DeliveryDetail>(`/deliveries/${deliveryId}/${action}`, { method: 'POST' }),
  );
}

/**
 * The one thing the wire shape needs doing to it.
 *
 * The storage driver hands back a root-relative path, which is correct
 * same-origin and broken the moment the API moves to its own host. Resolved
 * once here rather than at the `<img>` that draws it.
 */
function hydrate(delivery: DeliveryDetail): DeliveryDetail {
  return { ...delivery, proofOfDeliveryUrl: absoluteApiUrl(delivery.proofOfDeliveryUrl) };
}
